import { keyframes } from "@emotion/react";
import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";

// Two halves of one continuous 180deg turn, so a digit change reads as an
// odometer reel rather than two unrelated flips.
const rotateUpOut = keyframes`
  from {
    transform: translateY(0) rotateX(0deg);
    opacity: 1;
  }
  to {
    transform: translateY(-100%) rotateX(90deg);
    opacity: 0;
  }
`;

const rotateUpIn = keyframes`
  from {
    transform: rotateX(-90deg);
    opacity: 0;
  }
  to {
    transform: rotateX(0deg);
    opacity: 1;
  }
`;

const DURATION = "0.5s";
const EASING = "ease-in-out";

// Digits turn over when their value changes; punctuation renders statically.
const AnimatedDigit = ({ char }) => {
  const prevChar = useRef(char);
  const [outgoing, setOutgoing] = useState(null);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (prevChar.current !== char) {
      setOutgoing(prevChar.current);
      prevChar.current = char;
      setAnimKey((k) => k + 1);
    }
  }, [char]);

  if (!/[0-9]/.test(char)) {
    return (
      <Box component="span" sx={{ display: "inline-block" }}>
        {char}
      </Box>
    );
  }

  // Both digits share one grid cell so the outgoing one animates over the
  // incoming one. Absolute positioning would knock the number off the
  // baseline of the sentence around it.
  return (
    <Box component="span" sx={{ display: "inline-grid", perspective: 240 }}>
      {outgoing !== null && (
        <Box
          component="span"
          key={`out-${animKey}`}
          onAnimationEnd={() => setOutgoing(null)}
          sx={{
            gridArea: "1 / 1",
            backfaceVisibility: "hidden",
            transformOrigin: "bottom",
            animation: `${rotateUpOut} ${DURATION} ${EASING} forwards`,
          }}
        >
          {outgoing}
        </Box>
      )}
      <Box
        component="span"
        key={`in-${animKey}`}
        sx={{
          gridArea: "1 / 1",
          backfaceVisibility: "hidden",
          transformOrigin: "bottom",
          animation: `${rotateUpIn} ${DURATION} ${EASING}`,
        }}
      >
        {char}
      </Box>
    </Box>
  );
};

// Each digit turns independently, so an increment only animates the digits
// that actually changed. Tabular figures keep the width fixed mid-animation
// so the surrounding sentence doesn't shift.
const AnimatedCount = ({ value, sx }) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", fontVariantNumeric: "tabular-nums", ...sx }}
  >
    {value
      .toLocaleString()
      .split("")
      .map((char, i) => (
        <AnimatedDigit key={i} char={char} />
      ))}
  </Box>
);

export default AnimatedCount;
